import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { leaderboardStore } from '../stores/leaderboardStore'
import { authStore } from '../stores/authStore'

interface GameLeaderboardProps {
  game: string
  gameName: string
  emoji: string
  gradient?: string
  limit?: number
  currentScore?: number // Score actual de la partida en curso
}

const GameLeaderboard = observer(({ 
  game, 
  gameName, 
  emoji, 
  gradient = 'from-amber-500 to-orange-500',
  limit = 5,
  currentScore = 0
}: GameLeaderboardProps) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [localEntries, setLocalEntries] = useState(leaderboardStore.entries)

  useEffect(() => {
    leaderboardStore.fetchLeaderboard(game)
  }, [game])

  useEffect(() => {
    // Filter entries for this specific game
    const gameEntries = leaderboardStore.entries.filter(e => e.game === game)
    setLocalEntries(gameEntries)
  }, [leaderboardStore.entries, game])

  const entries = localEntries.slice(0, isExpanded ? 10 : limit)

  if (leaderboardStore.loading && localEntries.length === 0) {
    return (
      <div className="glass-card p-4 animate-pulse">
        <div className="flex items-center justify-center gap-2 text-gray-400">
          <span className="text-2xl animate-spin">🏆</span>
          <span>{t('leaderboard.loadingLeaderboard')}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="glass-card overflow-hidden">
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradient} p-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">{emoji}</span>
            <span className="text-white font-bold text-sm">{gameName} {t('gameLeaderboard.leaderboard')}</span>
          </div>
          <Link 
            to="/leaderboard" 
            className="text-white/80 text-xs hover:text-white transition-colors"
          >
            {t('leaderboard.viewAll')}
          </Link>
        </div>
      </div>

      {/* Entries */}
      {entries.length === 0 ? (
        <div className="p-6 text-center bg-gray-50">
          <span className="text-5xl block mb-2">🎮</span>
          <p className="text-gray-600 font-medium">{t('leaderboard.noScoresYet')}</p>
          <p className="text-gray-500 text-sm">{t('leaderboard.beFirstToPlay')}</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200 bg-white">
          {entries.map((entry, index) => {
            const rank = index + 1
            const isCurrentUser = entry.user_id === authStore.userId
            const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null
            
            // Mostrar score actual si está jugando
            const displayedScore = isCurrentUser && currentScore > entry.score ? currentScore : entry.score

            return (
              <div 
                key={entry.id || index}
                className={`flex items-center gap-3 p-3 transition-colors ${
                  isCurrentUser 
                    ? 'bg-gradient-to-r from-amber-100 via-yellow-100 to-orange-100' 
                    : 'hover:bg-gray-50'
                }`}
              >
                {/* Rank */}
                <div className="w-8 text-center">
                  {medal ? (
                    <span className="text-2xl">{medal}</span>
                  ) : (
                    <span className="text-gray-500 text-sm font-bold">#{rank}</span>
                  )}
                </div>

                {/* Avatar */}
                <div className="relative">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-2xl shadow-sm ${
                    isCurrentUser 
                      ? 'bg-gradient-to-br from-yellow-400 to-orange-500' 
                      : 'bg-gray-100 border border-gray-200'
                  }`}>
                    {entry.emoji || '👤'}
                  </div>
                  <span className="absolute -bottom-1 -right-1 text-sm drop-shadow">{entry.flag}</span>
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <p className={`text-base truncate ${
                    isCurrentUser ? 'text-orange-700 font-bold' : 'text-gray-800 font-medium'
                  }`}>
                    {entry.username || 'Anonymous'}
                    {isCurrentUser && (
                      <span className="text-orange-500 text-xs ml-1 font-normal">({t('common.you')})</span>
                    )}
                  </p>
                  {isCurrentUser && currentScore > 0 ? (
                    <p className="text-orange-500 text-sm font-medium">🎮 {t('gameLeaderboard.playing')}</p>
                  ) : (
                    <p className="text-gray-500 text-sm">{t('common.level')} {entry.level + 1}</p>
                  )}
                </div>

                {/* Score */}
                <div className="text-right">
                  <p className={`font-bold text-lg ${
                    isCurrentUser ? 'text-orange-600' : 'text-gray-900'
                  }`}>
                    {displayedScore.toLocaleString()}
                  </p>
                  <p className="text-gray-500 text-xs">{t('common.pts')}</p>
                  {isCurrentUser && currentScore > entry.score && (
                    <p className="text-green-500 text-[10px] font-bold">🔥 {t('gameLeaderboard.new')}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Expand/Collapse button */}
      {localEntries.length > limit && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full py-2 text-center text-gray-500 text-xs hover:bg-gray-50 transition-colors border-t border-gray-100"
        >
          {isExpanded ? t('gameLeaderboard.showLess') : t('gameLeaderboard.showMore', { total: localEntries.length })}
        </button>
      )}

      {/* CTA for empty state */}
      {entries.length === 0 && (
        <div className="p-3 border-t border-gray-100">
          <button
            onClick={() => window.location.reload()}
            className={`w-full py-2 bg-gradient-to-r ${gradient} text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity`}
          >
            {t('leaderboard.playNowToGetOnBoard')}
          </button>
        </div>
      )}
    </div>
  )
})

export default GameLeaderboard
